/**
 * Cognito authentication configuration.
 *
 * In production, values are loaded at runtime from /config.json (generated by CDK at deploy time).
 * In development, values come from VITE_COGNITO_* env vars.
 * Platform detection selects the correct redirect URI for web vs. native.
 */

import { logger } from '../../utils/logger.js'

const log = logger.create('auth-config')

interface RuntimeConfig {
  userPoolId: string
  clientId: string
  authDomain: string
  region: string
}

let config: RuntimeConfig | null = null

/** Load auth config from /config.json (production) or env vars (development). */
export async function loadAuthConfig(): Promise<void> {
  // Try env vars first (development)
  const envPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID
  const envClientId = import.meta.env.VITE_COGNITO_CLIENT_ID
  const envAuthDomain = import.meta.env.VITE_COGNITO_AUTH_DOMAIN

  if (envPoolId && envClientId && envAuthDomain) {
    config = {
      userPoolId: envPoolId,
      clientId: envClientId,
      authDomain: envAuthDomain,
      region: envPoolId.split('_')[0] || 'us-west-2',
    }
    log.debug('Auth config loaded from env vars')
    return
  }

  // Fall back to /config.json (production — generated by CDK, or bundled in public/ for native)
  try {
    const response = await fetch('/config.json')
    if (response.ok) {
      config = await response.json()
      log.debug('Auth config loaded from /config.json')
    }
  } catch (error) {
    log.debug('No auth config available:', error)
  }
}

const WEB_ORIGINS = ['https://taselith.com', 'https://www.taselith.com', 'https://app.taselith.com']
const NATIVE_SCHEME = 'com.taselith.app'

function isNativePlatform(): boolean {
  if (typeof window === 'undefined') return false
  const cap = (window as unknown as Record<string, unknown>).Capacitor as
    | { isNativePlatform?: () => boolean }
    | undefined
  return !!cap?.isNativePlatform?.()
}

function detectRedirectUri(): string {
  if (typeof window === 'undefined') return `${WEB_ORIGINS[0]}/auth/callback`

  if (isNativePlatform()) {
    return `${NATIVE_SCHEME}://auth/callback`
  }

  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return `${WEB_ORIGINS[0]}/auth/callback`
  }

  return `${window.location.origin}/auth/callback`
}

export const authConfig = {
  get region(): string {
    return config?.region ?? ''
  },

  get userPoolId(): string {
    return config?.userPoolId ?? ''
  },

  get clientId(): string {
    return config?.clientId ?? ''
  },

  get authDomain(): string {
    return config?.authDomain ?? ''
  },

  scopes: 'openid email profile aws.cognito.signin.user.admin',

  get redirectUri(): string {
    return detectRedirectUri()
  },

  get issuer(): string {
    return `https://cognito-idp.${this.region}.amazonaws.com/${this.userPoolId}`
  },

  get authorizeUrl(): string {
    return `https://${this.authDomain}/oauth2/authorize`
  },

  get tokenUrl(): string {
    return `https://${this.authDomain}/oauth2/token`
  },

  get revokeUrl(): string {
    return `https://${this.authDomain}/oauth2/revoke`
  },

  get userInfoUrl(): string {
    return `https://${this.authDomain}/oauth2/userInfo`
  },

  get isConfigured(): boolean {
    return config !== null && !!(config.userPoolId && config.clientId && config.authDomain)
  },
} as const
